// What happened around a session's flagged findings, as a few moments a
// reader can take in at once (lenses/FindingStory.jsx): the person's last ask
// before the first flagged message, the calls that failed on the way there,
// one flag for each finding holding the messages it cites, and the agent's
// answer to the person after the last, if it gave one before they spoke
// again. Everything is read from the
// record: labels are the call's own subject, the message's opening words or
// the finding's title, never a model's.
//
// Positions are indexes into `turns` (a page of the record, gaps included),
// not the record's turn numbers: the page starts mid-session.
import { buildTraceLanes, markKindOf } from "./traceLanes.js";
import { blockKind } from "./traceSearch.js";

const LABEL_MAX = 30;
const TITLE_MAX = 44;
const QUOTE_MAX = 4000;
const CONTEXT_MAX = 90;
// More messages than this under one finding keep the first and the last.
const MOMENTS_MAX = 8;
// A command beside the part of its result a check quoted, in a line.
const BRIEF_COMMAND_MAX = 60;
const FAILED_MAX = 3;

const str = (v) => (v == null ? "" : String(v));
const clip = (v, n) => {
    const s = str(v).replace(/\s+/g, " ").trim();
    return s.length > n ? `${s.slice(0, n - 3)}...` : s;
};
const idOf = (v) => str(v).trim().toLowerCase();
// What the harness wrapped around a message for the agent alone, which no
// person wrote or read: a reminder, a token count, a slash command's echo.
const INJECTED = /<(system-reminder|total_tokens|command-name|command-args|command-message|local-command-stdout|local-command-stderr)>[\s\S]*?<\/\1>/g;
const scrub = (s) => str(s).replace(INJECTED, "").replace(/<system-reminder>[\s\S]*$/, "").trim();
const textOf = (c) => scrub(typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join(" ") : "");
const NOTICE = /^\[[^\]\n]*(?:NOTIFICATION|NOT USER INPUT|AUTOMATED)[^\]\n]*\]|^<task-notification\b/i;
// A user-role turn holding words a person typed, not only what a harness sent
// in their role (a recap request, a compaction summary, another agent's
// message), by the rule the Context panel's User filter uses.
const typedByPerson = (t) => t?.role === "user" && (t.blocks || []).some((b) => b?.type === "text" && blockKind(t, b) === "user");

// What a call was asked to do: its command, path, address or query.
function subjectOf(block) {
    const inp = block?.input && typeof block.input === "object" ? block.input : {};
    const what = inp.command ?? inp.cmd ?? inp.file_path ?? inp.path ?? inp.url ?? inp.query ?? inp.pattern ?? inp.description;
    return what != null ? str(what) : block?.input != null ? JSON.stringify(block.input) : "";
}

// A turn as the record has it: who it is (person, notice, ran, result,
// failed, said, thought), and its call or text in a label, a line and in
// full. A call is quoted with what came back from it (`results`: call id ->
// its result), so a part a check quoted from the result reads beside the
// command that produced it.
function readTurn(turns, i, calls, results = null) {
    const t = turns[i];
    const blocks = Array.isArray(t?.blocks) ? t.blocks : [];
    const use = blocks.find((b) => b?.type === "tool_use");
    if (use) {
        const subject = subjectOf(use);
        const res = use.id ? results?.get(use.id) : null;
        const out = res ? textOf(res.content) : null;
        return {
            who: "ran", label: subject || use.name || "a call", line: `${use.name || "Call"} ${subject}`.trim(),
            quote: `${use.name || "Call"}: ${subject}${res ? `\n${res.isError ? "Failed: " : "-> "}${out || "(no output)"}` : ""}`.trim(),
            command: subject || null, output: out,
        };
    }
    const res = blocks.find((b) => b?.type === "tool_result");
    if (res) {
        const call = calls.get(res.tool_use_id);
        const subject = call ? subjectOf(call.block) : "";
        const out = textOf(res.content);
        return {
            who: res.isError ? "failed" : "result",
            label: `${res.isError ? "Failed: " : ""}${subject || call?.block?.name || "a result"}`,
            line: `${res.isError ? "failed: " : ""}${out || "(no output)"}`,
            quote: `${call ? `${call.block.name || "Call"}: ${subject}\n` : ""}${res.isError ? "Failed: " : "Returned: "}${out || "(no output)"}`,
            command: subject || null, output: out,
        };
    }
    const said = textOf(blocks.filter((b) => b?.type === "text").map((b) => b.text || ""));
    const thought = textOf(blocks.filter((b) => b?.type === "thinking").map((b) => b.thinking || b.text || ""));
    const text = said || thought;
    const person = t?.role === "user";
    const who = person ? (!said || NOTICE.test(said) || !typedByPerson(t) ? "notice" : "person") : said ? "said" : "thought";
    return { who, label: text ? `"${text}"` : "(no text)", line: text || "(no text)", quote: text || "(no text)" };
}

// The flagged findings' messages placed in these turns, each turn once:
// [{ turn, met, findings }], met when any finding citing it met a standard,
// findings the indexes of those citing it.
export function citedTurns(turns, findings) {
    const at = new Map();
    (turns || []).forEach((t, i) => { const id = idOf(t?.messageId); if (id && !at.has(id)) at.set(id, i); });
    const out = new Map();
    (findings || []).forEach((f, k) => {
        for (const id of f?.messageIds || []) {
            const i = at.get(idOf(id));
            if (i == null) continue;
            const cur = out.get(i) || { turn: i, met: false, findings: [] };
            cur.met = cur.met || !!f.met;
            if (!cur.findings.includes(k)) cur.findings.push(k);
            out.set(i, cur);
        }
    });
    return [...out.values()].sort((a, b) => a.turn - b.turn);
}

// A reader of one page of turns: a moment at any turn, { turn, at, who,
// label, quote, highlight, before, after, messageId }, or null for a gap or
// no turn. `quotes`: message id -> what a check quoted from it: the part of
// the turn's own text to highlight, or, where the text does not hold it, the
// quote itself. The lines either side skip what no person saw.
function momentReader(list, quotes = null) {
    const calls = new Map();
    const results = new Map();
    list.forEach((t, j) => {
        for (const b of t?.blocks || []) {
            if (b?.type === "tool_use" && b.id) calls.set(b.id, { block: b, i: j });
            if (b?.type === "tool_result" && b.tool_use_id && !results.has(b.tool_use_id)) results.set(b.tool_use_id, b);
        }
    });
    const live = (j) => list[j] && list[j].role !== "gap";
    const read = (j) => readTurn(list, j, calls, results);
    const near = (i, step) => {
        for (let j = i + step; j >= 0 && j < list.length; j += step) {
            if (live(j) && read(j).who !== "notice") return j;
        }
        return null;
    };
    const line = (j) => (j == null ? null : clip(read(j).line, CONTEXT_MAX));
    return (i) => {
        if (!live(i)) return null;
        const r = read(i);
        const quoted = clip(quotes?.get(idOf(list[i].messageId)) || "", QUOTE_MAX);
        const text = clip(r.quote, QUOTE_MAX);
        const held = quoted && text.includes(quoted);
        return {
            turn: i, at: Number.isFinite(list[i].messageAt) ? list[i].messageAt : null, who: r.who, label: clip(r.label, LABEL_MAX),
            quote: quoted && !held ? quoted : text, highlight: held ? quoted : null, quoted: !!quoted,
            brief: briefOf(r, held ? quoted : null),
            before: line(near(i, -1)), after: line(near(i, 1)), messageId: list[i].messageId || null,
        };
    };
}

// A message in a line, where a check quoted part of it: that part, with the
// command it came back from when it came back from one. Null otherwise.
function briefOf(r, quoted) {
    if (!quoted) return null;
    const whole = clip(r.command || "", QUOTE_MAX);
    if (!whole || whole.includes(quoted)) return quoted;
    return `${clip(lastStatement(r.command), BRIEF_COMMAND_MAX)} -> ${quoted}`;
}

// A script's last statement that does something: what produced its output,
// where the script first sets things up (a PR body, a variable) and only
// then runs the command.
function lastStatement(command) {
    const parts = str(command).split(/\r?\n|;|&&/).map((p) => p.trim()).filter((p) => p.length >= 6 && /[A-Za-z]{2,}/.test(p) && !/^['"]?@['"]?$/.test(p));
    return parts.length ? parts[parts.length - 1] : str(command);
}

// One turn of these, as a moment's panel reads it.
export function turnMoment(turns, i) {
    return momentReader(Array.isArray(turns) ? turns : [])(i);
}

const keepEnds = (list, n) => (list.length <= n ? list : [...list.slice(0, Math.ceil(n / 2)), ...list.slice(-Math.floor(n / 2))]);

// -> null when no flagged message is in these turns, else
// { from, to, flags, steps, met }:
//   from/to: the stretch of turns drawn
//   flags: [{ key, kind: ask|failed|finding|told, turn, label, met,
//             finding?, title?, standard?, why?, moments }], in order of their
//          first moment; a finding
//          flag's moments are the messages it cites, the others' their one
//   steps: [{ flag, moment }], every moment in the order the arrow keys
//          step through them; a message several findings cite is one step,
//          under the first
// `findings`: [{ title, messageIds, met, quotes: [{ messageId, text }],
// standard, why }], the standard that met it and its reason.
export function findingStory(turns, findings) {
    const list = Array.isArray(turns) ? turns : [];
    const cited = citedTurns(list, findings);
    if (!cited.length) return null;
    const live = (i) => list[i] && list[i].role !== "gap";
    const firstCited = cited[0].turn;
    const lastCited = cited[cited.length - 1].turn;

    let model = null;
    try { model = buildTraceLanes(list); } catch { model = null; }
    const lanes = model?.lanes || [];
    const items = (domain) => lanes.filter((l) => l.domain === domain).flatMap((l) => l.items || [])
        .filter((it) => Number.isInteger(it.turnIndex) && live(it.turnIndex)).sort((a, b) => a.turnIndex - b.turnIndex);

    const quotes = new Map();
    for (const f of findings || []) for (const q of f?.quotes || []) if (q?.messageId && q.text && !quotes.has(idOf(q.messageId))) quotes.set(idOf(q.messageId), str(q.text));
    const momentAt = momentReader(list, quotes);
    const callTurnOf = (i) => {
        const res = (list[i]?.blocks || []).find((b) => b?.type === "tool_result");
        if (!res) return i;
        const at = list.findIndex((t) => (t?.blocks || []).some((b) => b?.type === "tool_use" && b.id === res.tool_use_id));
        return at >= 0 ? at : i;
    };
    const resultOf = (i) => {
        const id = (list[i]?.blocks || []).find((b) => b?.type === "tool_use")?.id;
        const at = id ? list.findIndex((t) => (t?.blocks || []).some((b) => b?.type === "tool_result" && b.tool_use_id === id)) : -1;
        return at >= 0 ? at : i;
    };

    const asked = (it) => it.op?.kind !== "notice" && typedByPerson(list[it.turnIndex]);
    const asks = items("prompt").filter((it) => asked(it) && it.turnIndex <= firstCited);
    const ask = asks[asks.length - 1] || null;
    // The agent's answer to the person, if it gave one before they spoke
    // again: a reply after that answers something else.
    const nextAsk = items("prompt").find((it) => asked(it) && it.turnIndex > lastCited);
    const told = items("reply").find((it) => it.turnIndex > lastCited && (!nextAsk || it.turnIndex < nextAsk.turnIndex)) || null;
    const citedAt = new Set(cited.map((c) => c.turn));
    // With no ask before it on these turns, failures from the page's start.
    const startAt = ask ? ask.turnIndex : -1;
    const failedTurns = [...new Set(lanes.flatMap((l) => l.items || [])
        .filter((it) => markKindOf(it) === "error" && Number.isInteger(it.turnIndex) && it.turnIndex > startAt && it.turnIndex < lastCited && live(it.turnIndex))
        .map((it) => it.turnIndex))]
        .filter((t) => !citedAt.has(t) && ![...citedAt].some((c) => callTurnOf(c) === t))
        .sort((a, b) => a - b);
    // The failures nearest the first flagged message say most about it.
    const failed = failedTurns.filter((t) => t < firstCited).slice(-FAILED_MAX);

    const one = (kind, turn, moment, extra = {}) => ({ key: `${kind}:${turn}`, kind, turn, label: moment.label, met: false, moments: [moment], ...extra });
    const flags = [];
    // An ask a finding cites is one of its moments already: it stands there
    // once, not again as the ask.
    if (ask && !citedAt.has(ask.turnIndex)) {
        const m = momentAt(ask.turnIndex);
        flags.push(one("ask", ask.turnIndex, { ...m, label: clip(`"${ask.op?.text || m.quote}"`, LABEL_MAX), quote: clip(ask.op?.text || m.quote, QUOTE_MAX), who: "person" }));
    }
    // A failure's mark stands at its call; its result says what failed.
    for (const t of failed) {
        const m = momentAt(resultOf(t));
        flags.push(one("failed", t, { ...m, turn: t }, { label: momentAt(t)?.label || m.label }));
    }
    (findings || []).forEach((f, k) => {
        const mine = cited.filter((c) => c.findings.includes(k));
        if (!mine.length) return;
        const moments = keepEnds(mine, MOMENTS_MAX).map((c) => momentAt(c.turn)).filter(Boolean);
        if (!moments.length) return;
        flags.push({
            key: `finding:${k}`, kind: "finding", finding: k, turn: moments[0].turn, met: !!f.met, moments,
            label: clip(f.title || "Flagged finding", TITLE_MAX), title: str(f.title || "Flagged finding"),
            standard: f.standard || null, why: f.why || null,
        });
    });
    if (told) {
        const m = momentAt(told.turnIndex);
        flags.push(one("told", told.turnIndex, { ...m, label: clip(`"${told.op?.text || m.quote}"`, LABEL_MAX), quote: clip(told.op?.text || m.quote, QUOTE_MAX), who: "said" }));
    }
    flags.sort((a, b) => a.turn - b.turn || (a.kind === "finding") - (b.kind === "finding"));
    const stepped = new Set();
    const steps = flags.flatMap((f, i) => f.moments.map((m, j) => ({ flag: i, moment: j, turn: m.turn })))
        .filter((s) => !stepped.has(s.turn) && stepped.add(s.turn))
        .map(({ flag, moment }) => ({ flag, moment }));

    const from = ask ? ask.turnIndex : (failed[0] ?? firstCited);
    const to = told ? told.turnIndex : lastCited;
    return { from, to, flags, steps, met: cited.some((c) => c.met) };
}

// ---------------------------------------------------------------------------
// The story as one compact timeline (lenses/FindingStory.jsx): the moments a
// reader needs open, and the agent's other work between two of them folded
// into one row. Everything comes from the page's own turns, in their order;
// nothing is worked out from a command's text but the exit code it printed.

// Calls folded after the last flagged message, at most.
const AFTER_CALLS_MAX = 25;
const COMMAND_MAX = 6000;
const RESULT_MAX = 4000;
// A pause between two moments this long is said on the timeline.
const GAP_MS = 10 * 60000;

// What came back, its lines kept: a result's text parts, one a line.
const resultText = (c) => scrub(typeof c === "string" ? c : Array.isArray(c) ? c.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n") : "");
// Cut to `n` characters, its lines kept.
const cut = (v, n) => { const s = str(v); return s.length > n ? `${s.slice(0, n - 3)}...` : s; };
const unquote = (raw) => { try { return JSON.parse(`"${raw}"`); } catch { return raw; } };
// A call's command in full: the shell command, a Codex code-mode script's
// commands one a line, a lone argument, or its arguments.
function commandOf(block) {
    const inp = block?.input;
    const keys = inp && typeof inp === "object" ? Object.keys(inp) : [];
    const raw = typeof inp === "string" ? inp : keys.length ? (inp.command ?? inp.cmd ?? inp.code ?? inp.script ?? (keys.length === 1 ? inp[keys[0]] : null)) : null;
    const text = typeof raw === "string" ? raw : Array.isArray(raw) ? raw.join(" ") : inp != null ? JSON.stringify(inp, null, 1) : "";
    const cmds = /exec_command\(/.test(text) ? [...text.matchAll(/\bcmd:\s*"((?:[^"\\]|\\.)*)"/g)].map((m) => unquote(m[1])) : [];
    return cmds.length ? cmds.join("\n") : text;
}
// What came back, without the wrapper Codex puts round a script's output.
function cleanOutput(text) {
    const s = str(text);
    if (/^(Chunk ID:|Process exited with code|Exit code:)/m.test(s) && /\nOutput:/.test(s)) {
        const code = (s.match(/(?:Process exited with code|Exit code:)\s*(-?\d+)/) || [])[1];
        const body = s.replace(/^[\s\S]*?\nOutput:\n?/, "").trim();
        return `${body}${code != null ? `${body ? "\n" : ""}exit code ${code}` : ""}`;
    }
    if (/^Script completed/.test(s) || /^\{"chunk_id"/.test(s)) {
        const outs = [...s.matchAll(/"output"\s*:\s*"((?:[^"\\]|\\.)*)"/g)].map((m) => unquote(m[1])).filter((x) => x.trim());
        const codes = [...s.matchAll(/"exit_code"\s*:\s*(-?\d+)/g)].map((m) => m[1]);
        return `${outs.join("\n")}${codes.length ? `${outs.length ? "\n" : ""}exit code ${codes.join(", ")}` : ""}`;
    }
    return s;
}
// The exit code a result printed, as it printed it; null where it printed none.
export function exitCodeOf(text) {
    const m = str(text).match(/\bexit code:? (-?\d+)|\bProcess exited with code (-?\d+)|\brc (-?\d+)\b/i);
    return m ? (m[1] ?? m[2] ?? m[3]) : null;
}
// Codex's exec_command carries a reason the agent writes; with
// sandbox_permissions "require_escalated" it is the text of its request to
// run outside the sandbox.
function reasonOf(block) {
    const inp = block?.input && typeof block.input === "object" ? block.input : null;
    const text = inp ? "" : str(block?.input);
    const reason = inp?.justification ?? (text.match(/"justification"\s*:\s*"((?:[^"\\]|\\.)*)"/) || [])[1] ?? null;
    if (!reason) return null;
    const escalated = inp ? inp.sandbox_permissions === "require_escalated" : /"sandbox_permissions"\s*:\s*"require_escalated"/.test(text);
    return { text: inp ? str(reason) : unquote(reason), escalated };
}
// Whose words a user-role message is when no person typed it: another
// session's message names its sender, an automation names itself.
function voiceOf(t) {
    const raw = (t?.blocks || []).filter((b) => b?.type === "text").map((b) => str(b.text)).join("\n");
    const peer = raw.match(/<cross-session-message\b([^>]*)>([\s\S]*?)(?:<\/cross-session-message>|$)/);
    if (peer) {
        const name = (peer[1].match(/\bname="([^"]*)"/) || [])[1];
        return { from: name ? `Another agent \u00b7 ${name}` : "Another agent", text: peer[2].trim() };
    }
    const auto = raw.match(/<heartbeat>[\s\S]*?<automation_id>([^<]*)<\/automation_id>[\s\S]*?<instructions>([\s\S]*?)(?:<\/instructions>|$)/);
    if (auto) return { from: `Automation \u00b7 ${auto[1].trim()}`, text: auto[2].trim() };
    return { from: "Not typed by the person", text: textOf(raw) || raw.trim() };
}

// -> null without a story, else { rows, openAt }:
//   rows, in page order, each one of
//     { kind: "say", turn, at, messageId, text, person, from?, cited, marks }
//     { kind: "act", turn, at, messageId, tool, command, result, failed, exitCode, reason, tone, marks }
//     { kind: "said", turn, at, messageId, text, cited, tone?, marks }
//     { kind: "calls", items: [{ kind: "call", turn, at, tool, command, result, failed, exitCode } | { kind: "said", turn, at, text }] }
//     { kind: "gap", ms }
//   openAt: the message the workspace opens at, the first flagged one.
// It runs from the story's start to the next words the person typed after
// the last flagged message, those included. `marks`: what a check quoted
// from that message (or, for a call, from what came back).
export function storyTimeline(turns, findings, story) {
    const list = Array.isArray(turns) ? turns : [];
    const cited = citedTurns(list, findings);
    if (!story || !cited.length) return null;
    const live = (i) => list[i] && list[i].role !== "gap";
    const metAt = new Map(cited.map((c) => [c.turn, c.met]));
    const citedAt = new Set(cited.map((c) => c.turn));
    const quotes = new Map();
    for (const f of findings || []) for (const q of f?.quotes || []) if (q?.messageId && q.text) quotes.set(idOf(q.messageId), [...(quotes.get(idOf(q.messageId)) || []), str(q.text)]);
    const marksIn = (...ids) => ids.filter(Boolean).flatMap((id) => quotes.get(idOf(id)) || []);
    const callAt = new Map();
    const resultAt = new Map();
    list.forEach((t, j) => {
        for (const b of t?.blocks || []) {
            if (b?.type === "tool_use" && b.id && !callAt.has(b.id)) callAt.set(b.id, j);
            if (b?.type === "tool_result" && b.tool_use_id && !resultAt.has(b.tool_use_id)) resultAt.set(b.tool_use_id, j);
        }
    });
    // One message can carry several calls (made in parallel), and their
    // results ride several messages: everything here goes by the call.
    const usesIn = (j) => (list[j]?.blocks || []).filter((b) => b?.type === "tool_use");
    const resultsIn = (j) => (list[j]?.blocks || []).filter((b) => b?.type === "tool_result");
    const atOf = (j) => (Number.isFinite(list[j]?.messageAt) ? list[j].messageAt : null);
    const call = (j, use) => {
        const rj = use?.id ? resultAt.get(use.id) ?? null : null;
        const res = rj != null ? (list[rj].blocks || []).find((b) => b?.type === "tool_result" && b.tool_use_id === use.id) : null;
        // The exit code is read before the cut, which can drop the line printing it.
        const whole = res ? cleanOutput(resultText(res.content)) : null;
        return { turn: j, id: use?.id || null, at: atOf(j), tool: use?.name || "call", command: cut(commandOf(use), COMMAND_MAX), result: whole == null ? null : cut(whole, RESULT_MAX), failed: !!res?.isError, exitCode: exitCodeOf(whole), resultTurn: rj, reason: reasonOf(use) };
    };
    // The calls the findings point at. A flagged result stands at its own
    // call. Where the flagged message holds several calls or results, only
    // those a check quoted from (the call, or what came back) are flagged;
    // with nothing quoted, all of them.
    const acts = new Set();
    // A message a review cites without a quote of its own is read with the
    // quotes the check took from the same findings' other messages.
    const quotesOf = (k) => (findings?.[k]?.quotes || []).map((q) => str(q?.text)).filter(Boolean);
    for (const c of cited) {
        const own = marksIn(list[c.turn].messageId);
        const qs = own.length ? own : c.findings.flatMap(quotesOf);
        const ids = [
            ...resultsIn(c.turn).map((r) => r.tool_use_id).filter((id) => callAt.has(id)),
            ...usesIn(c.turn).map((u) => u.id).filter(Boolean),
        ];
        // A quote can come from any of the call's arguments (a shell call's
        // description, not only its command) or from what came back.
        const quoted = ids.filter((id) => {
            const j = callAt.get(id);
            const use = usesIn(j).find((u) => u.id === id);
            const x = call(j, use);
            const args = typeof use?.input === "string" ? use.input : Object.values(use?.input || {}).map((v) => (typeof v === "string" ? v : JSON.stringify(v))).join("\n");
            return qs.some((q) => x.command.includes(q) || args.includes(q) || (x.result || "").includes(q));
        });
        for (const id of quoted.length ? quoted : ids) acts.add(id);
    }
    const actTurns = [...acts].map((id) => callAt.get(id));
    const firstAct = actTurns.length ? Math.min(...actTurns) : cited[0].turn;
    const lastCited = cited[cited.length - 1].turn;
    let end = list.length - 1;
    for (let j = lastCited + 1; j < list.length; j++) if (live(j) && typedByPerson(list[j])) { end = j; break; }
    const told = story.flags?.find((f) => f.kind === "told")?.turn ?? null;
    const start = Math.min(story.from ?? cited[0].turn, firstAct, cited[0].turn);

    const rows = [];
    let fold = [];
    let afterCalls = 0;
    let lastAt = null;
    const flush = () => { if (fold.length) rows.push({ kind: "calls", items: fold }); fold = []; };
    // A moment opens its own row; a long pause before it is said first.
    const open = (j, row) => {
        const at = atOf(j);
        flush();
        if (at != null && lastAt != null && at - lastAt >= GAP_MS) rows.push({ kind: "gap", ms: at - lastAt });
        rows.push(row);
    };
    for (let j = start; j <= end; j++) {
        if (!live(j)) continue;
        const t = list[j];
        if (atOf(j) != null) lastAt = lastAt == null ? atOf(j) : lastAt;
        const uses = usesIn(j);
        if (uses.length) {
            for (const use of uses) {
                const c = call(j, use);
                // The record can hold one call twice, under two message ids: it is told once.
                const twice = c.id && acts.has(c.id) && rows.some((r) => r.kind === "act" && r.tool === c.tool && r.command === c.command && r.result === c.result);
                if (twice) continue;
                if (c.id && acts.has(c.id)) {
                    const met = metAt.get(j) || (c.resultTurn != null && metAt.get(c.resultTurn));
                    open(j, { kind: "act", ...c, messageId: t.messageId || null, tone: met ? "danger" : "warn", marks: marksIn(t.messageId, c.resultTurn != null ? list[c.resultTurn].messageId : null) });
                } else if (j < lastCited || afterCalls++ < AFTER_CALLS_MAX) {
                    fold.push({ kind: "call", ...c });
                }
            }
            if (atOf(j) != null) lastAt = atOf(j);
            continue;
        }
        if (resultsIn(j).length) continue;
        const r = readTurn(list, j, new Map());
        if (t.role === "user") {
            const person = r.who === "person";
            if (!person && !citedAt.has(j)) continue;
            const v = person ? null : voiceOf(t);
            open(j, { kind: "say", turn: j, at: atOf(j), messageId: t.messageId || null, text: person ? r.quote : v.text, person, ...(v ? { from: v.from } : {}), cited: citedAt.has(j), marks: marksIn(t.messageId) });
        } else if (r.who === "said") {
            if (citedAt.has(j) || j === told) {
                const isCited = citedAt.has(j);
                open(j, { kind: "said", turn: j, at: atOf(j), messageId: t.messageId || null, text: r.quote, cited: isCited, ...(isCited ? { tone: metAt.get(j) ? "danger" : "warn" } : {}), marks: marksIn(t.messageId) });
            } else {
                fold.push({ kind: "said", turn: j, at: atOf(j), text: r.quote });
            }
        } else continue;
        if (atOf(j) != null) lastAt = atOf(j);
    }
    flush();
    return { rows, openAt: list[cited[0].turn]?.messageId || null };
}
